import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../config";
import { formatMobile, formatName, formatPhone } from "../../../lib/Misc";
import { debounce } from "lodash";
import MainBody from "../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../components/Pagination";
import sortCaret from "../../../components/SortCaret";
import RegisteredUserActionButton from "./RegisteredUserActionButton";
import moment from "moment";
import RegisteredUserDetail from "./RegisteredUserDetail";
import Confirmation from "../../../components/Confirmation";

const RegisteredUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [userId, setUserId] = useState(null);
  const [showView, setShowView] = useState(false);
  const [showUserResend, setShowUserResend] = useState(false);

  const toggleShowModal = () => {
    if (showView) {
      setUserId();
    }
    setShowView(!showView);
  };

  const toggleShowResend = () => {
    if (showUserResend) {
      setUserId(null);
    }
    setShowUserResend(!showUserResend);
  };

  const fetchUser = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_REGISTERED_USER_FOR_ADMIN}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const usersList = data.data.data;
        setUsersList(usersList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setUsersList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField]
  );

  const usersGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: usersList[i]?.id,
        id: usersList[i]?.id ? usersList[i]?.id : "",
        name: formatName(usersList[i]?.first_name, usersList[i]?.last_name),
        email: usersList[i]?.email,
        phone: usersList[i]?.phone ? formatPhone(usersList[i]?.phone) : "-",
        mobile: usersList[i]?.mobile ? formatMobile(usersList[i]?.mobile) : "-",
        role: usersList[i]?.role?.name ? usersList[i]?.role?.name : "",
        registered_date: usersList[i]?.created_at
          ? moment(usersList[i]?.created_at, "YYYY-MM-DD HH:mm:SS").format(
              "DD-MM-YYYY"
            )
          : "-",
        last_login_date: usersList[i]?.last_login_date
          ? usersList[i]?.last_login_date
          : "-",
        action: (
          <>
            <RegisteredUserActionButton
              id={usersList[i]?.id}
              status={usersList[i]?.status}
              setUserId={setUserId}
              toggleShowModal={toggleShowModal}
              toggleShowResend={toggleShowResend}
            />
          </>
        ),
      });
    }
    return items;
  };

  const users = usersGenerator(usersList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "email",
      text: "Email",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "phone",
      text: "Phone No.",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "mobile",
      text: "Mobile No.",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "role",
      text: "Role",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "registered_date",
      text: "Registered Date",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "last_login_date",
      text: "Last Login Date",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchUser(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchUser(limit, page);
  };

  const handleResendEmailVerification = async () => {
    try {
      setLoading(true);
      await get(`${API.REGISTERED_USER_FOR_ADMIN_RESEND_EMAIL}/${userId}`);
      setLoading(false);
      fetchUser();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap"></div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={users}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>
      {showView && (
        <RegisteredUserDetail
          userId={userId}
          toggleShowModal={toggleShowModal}
          showView={showView}
          setUserId={setUserId}
          setShowView={setShowView}
        />
      )}

      <Confirmation
        title={"Resend Email Verification"}
        open={showUserResend}
        setOpen={setShowUserResend}
        toggleShow={() => {
          toggleShowResend();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to resend email verification?"}
        okAction={async () => {
          handleResendEmailVerification();
          toggleShowResend();
        }}
        cancelAction={() => {
          setUserId(null);
          setShowUserResend(!showUserResend);
        }}
      />
    </>
  );
};

export default RegisteredUsers;
